<template>
  <div class="contact-task-view">
    <van-sticky z-index="99">
      <div class="header">
        <div class="header-left">
          <h1 class="page-title">Contact Tasks</h1>
          <p class="page-subtitle">{{ activeTab === 'tasks' ? 'Available tasks to complete' : 'Manage your task orders' }}</p>
        </div>
      </div>

      <van-tabs
        v-model:active="activeTab"
        line-width="32px"
        line-height="4px"
        class="custom-tabs"
        :border="false"
      >
        <van-tab title="Available Tasks" name="tasks"/>
        <van-tab title="My Orders" name="orders"/>
      </van-tabs>
    </van-sticky>

    <!-- 接单 Tab - 可接任务列表 -->
    <div v-if="activeTab === 'tasks'" class="tasks-tab">
      <van-list
        v-model:loading="taskLoading"
        :finished="taskFinished"
        loading-text="Loading..."
        :finished-text="taskList.length > 0 ? 'No more tasks available' : ''"
        @load="onLoadTasks"
        class="task-list"
      >
        <div v-if="taskLoading && taskList.length === 0" class="skeletons">
          <van-skeleton v-for="i in 3" :key="i" title avatar :row="3" style="margin-bottom: 20px"/>
        </div>

        <div v-for="(task, index) in taskList" :key="index" class="task-card">
          <!-- 卡片头部：批次ID和状态 -->
          <div class="card-header">
            <div class="batch-info">
              <van-tag type="primary" size="medium" class="batch-tag">{{ task.batchId }}</van-tag>
              <van-tag type="success" size="small" class="status-tag">Available</van-tag>
              <div class="task-detail-toggle-inline" @click="toggleTaskDetail(task.id)">
                <van-icon name="info-o" class="toggle-info-icon-small"/>
                <span class="toggle-label-small">Details</span>
                <van-icon 
                  :name="expandedTasks.has(task.id) ? 'arrow-up' : 'arrow-down'" 
                  class="toggle-icon-small"
                  :class="{ 'expanded': expandedTasks.has(task.id) }"
                />
              </div>
            </div>
          </div>
          
          <!-- 奖励区域：优化设计 -->
          <div class="reward-highlight">
            <div class="reward-highlight-content">
              <div class="reward-main-section">
                <div class="reward-icon-wrapper">
                  <span class="reward-currency-icon">$</span>
                </div>
                <div class="reward-amount-section">
                  <div class="reward-label-row">
                    <span class="reward-highlight-label">Reward</span>
                  </div>
                  <div class="reward-amount-row">
                    <span class="reward-currency">{{ getCurrency() }}</span>
                    <span class="reward-highlight-amount">{{ formatMoney(calculateTotalReward(task)) }}</span>
                    <span class="reward-equals">=</span>
                    <span class="reward-account-count">{{ task.totalAccountCount - task.receivedAccountCount }}</span>
                    <span class="reward-multiply">×</span>
                    <span class="reward-unit-price">{{ formatMoney(getTaskUnitPrice(task)) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <transition name="slide-fade">
            <div 
              v-show="expandedTasks.has(task.id)" 
              class="card-body task-detail-content"
            >
            <div class="task-info-compact">
              <div class="info-row-compact">
                  <div class="info-icon-wrapper">
                <van-icon name="description" class="info-icon-small"/>
                  </div>
                  <div class="info-content-wrapper">
                    <span class="info-label-compact">Description</span>
                <span class="info-value-compact">{{ task.taskDesc || 'No description' }}</span>
              </div>
                </div>
                
              <div class="info-row-compact">
                  <div class="info-icon-wrapper">
                <van-icon name="chat-o" class="info-icon-small"/>
                  </div>
                  <div class="info-content-wrapper">
                    <span class="info-label-compact">Speech</span>
                <span class="info-value-compact speech-preview">{{ getSpeechPreview(task.speechSkills) }}</span>
              </div>
                </div>
                
              <div class="info-row-compact">
                  <div class="info-icon-wrapper">
                <van-icon name="friends-o" class="info-icon-small"/>
                  </div>
                  <div class="info-content-wrapper">
                    <span class="info-label-compact">Accounts</span>
                    <div class="accounts-info-inline">
                      <span class="accounts-item-inline">
                        <span class="accounts-label-inline">Total</span>
                        <strong class="accounts-value-inline">{{ task.totalAccountCount }}</strong>
                      </span>
                      <span class="accounts-divider-inline">|</span>
                      <span class="accounts-item-inline">
                        <span class="accounts-label-inline">Received</span>
                        <strong class="accounts-value-inline">{{ task.receivedAccountCount }}</strong>
                      </span>
                      <span class="accounts-divider-inline">|</span>
                      <span class="accounts-item-inline available-item-inline">
                        <span class="accounts-label-inline">Available</span>
                        <strong class="accounts-value-inline available-value-inline">{{ task.totalAccountCount - task.receivedAccountCount }}</strong>
                </span>
              </div>
                  </div>
                </div>
                
              <div class="info-row-compact">
                  <div class="info-icon-wrapper">
                <van-icon name="orders-o" class="info-icon-small"/>
                  </div>
                  <div class="info-content-wrapper">
                    <span class="info-label-compact">Batch</span>
                <span class="info-value-compact">{{ task.batchAccountCount }} per order</span>
              </div>
            </div>
          </div>
            </div>
          </transition>

          <!-- 接单按钮：更吸引人的设计 -->
          <div class="card-footer">
            <van-button
              type="primary"
              round
              block
              size="large"
              :loading="receivingTaskId === task.id"
              @click="handleReceive(task)"
              :disabled="(task.totalAccountCount - task.receivedAccountCount) <= 0"
              class="receive-task-btn"
            >
              <van-icon name="add-o" v-if="receivingTaskId !== task.id" style="margin-right: 4px"/>
              {{ receivingTaskId === task.id ? 'Receiving...' : 'Receive Task' }}
            </van-button>
          </div>
        </div>

        <van-empty v-if="!taskLoading && taskList.length === 0" description="No tasks available"/>
      </van-list>
    </div>

    <!-- 做单 Tab - 我的订单列表 -->
    <div v-if="activeTab === 'orders'" class="orders-tab">
      <van-tabs
        v-model:active="orderStateTab"
        line-width="32px"
        line-height="4px"
        class="order-state-tabs"
        :border="false"
      >
        <van-tab title="Received" name="received"/>
        <van-tab title="Submitted" name="submitted"/>
        <van-tab title="Passed" name="passed"/>
        <van-tab title="Rejected" name="rejected"/>
      </van-tabs>

      <van-list
        v-model:loading="orderLoading"
        :finished="orderFinished"
        loading-text="Loading..."
        :finished-text="ordersList.length > 0 ? 'No more orders' : ''"
        @load="onLoadOrders"
        class="order-list"
      >
        <div v-for="(order, index) in ordersList" :key="index" class="order-card" @click="handleViewOrder(order)">
          <div class="card-header-order">
            <div class="order-header-left">
              <div class="order-no-row">
                <span class="order-label">Order:</span>
                <span class="order-number">{{ order.orderNo }}</span>
              </div>
              <div class="batch-id-row" v-if="order.task?.batchId">
                <span class="batch-label">Batch:</span>
                <span class="batch-number">{{ order.task.batchId }}</span>
              </div>
            </div>
            <div class="status-badge-order" :class="getStatusClass(order.orderState)">
              <van-icon :name="getStatusIcon(order.orderState)"/>
              <span>{{ getStatusText(order.orderState) }}</span>
            </div>
          </div>

          <div class="card-body-order">
            <div class="info-row-order accounts-row" @click.stop="toggleAccounts(order.id)">
              <van-icon name="friends-o" class="info-icon-order"/>
              <span class="label-order">Accounts</span>
              <div class="accounts-container">
                <span class="accounts-count">{{ getAccountCount(order.accounts) }} accounts</span>
                <van-icon 
                  :name="expandedAccounts.has(order.id) ? 'arrow-up' : 'arrow-down'" 
                  class="expand-icon"
                />
              </div>
            </div>
            <div v-if="expandedAccounts.has(order.id)" class="accounts-detail">
              <div 
                v-for="(account, idx) in getAccountList(order.accounts)" 
                :key="idx"
                class="account-item-row"
              >
                <span class="account-index">{{ idx + 1 }}.</span>
                <span class="account-name">{{ account }}</span>
                <van-tag 
                  v-if="getAccountStatus(order.id, account)" 
                  :type="getAccountStatusTagType(getAccountStatus(order.id, account))"
                  size="mini"
                >
                  {{ getAccountStatusText(getAccountStatus(order.id, account)) }}
                </van-tag>
              </div>
            </div>

            <div class="info-row-order" v-if="order.task && order.task.taskDesc">
              <van-icon name="description" class="info-icon-order"/>
              <span class="label-order">Description</span>
              <span class="value-order">{{ order.task.taskDesc }}</span>
            </div>

            <div class="info-row-order reward-row" v-if="order.task">
              <span class="info-icon-order reward-currency-icon-order">$</span>
              <span class="label-order">Reward</span>
              <div class="reward-calculation">
                <span class="reward-formula">
                  {{ formatMoney(getOrderUnitPrice(order)) }} × {{ getAccountCount(order.accounts) }}
                </span>
                <span class="reward-equals">=</span>
                <span class="reward-total">{{ formatMoney(calculateOrderReward(order)) }}</span>
              </div>
            </div>

            <div class="info-row-order" v-if="order.submitTime">
              <van-icon name="clock-o" class="info-icon-order"/>
              <span class="label-order">Submit Time</span>
              <span class="value-order">{{ formatTime(order.submitTime) }}</span>
            </div>

            <div class="info-row-order" v-if="order.auditTime">
              <van-icon name="checked" class="info-icon-order"/>
              <span class="label-order">Audit Time</span>
              <span class="value-order">{{ formatTime(order.auditTime) }}</span>
            </div>

            <div class="info-row-order" v-if="order.actualAmount && order.actualAmount !== order.expectedAmount">
              <span class="info-icon-order reward-currency-icon-order">$</span>
              <span class="label-order">Actual Reward</span>
              <span class="value-order actual-amount">{{ formatMoney(order.actualAmount) }}</span>
            </div>

            <div class="info-row-order" v-if="order.auditRemark">
              <van-icon name="comment-o" class="info-icon-order"/>
              <span class="label-order">Audit Remark</span>
              <span class="value-order">{{ order.auditRemark }}</span>
            </div>
          </div>

          <div class="card-footer-order" v-if="order.orderState === 'received'">
            <van-button
              type="primary"
              round
              block
              size="large"
              icon="photograph"
              @click.stop="handleUploadEvidence(order)"
              class="upload-evidence-btn"
            >
              Upload Evidence
            </van-button>
          </div>
        </div>

        <van-empty v-if="!orderLoading && ordersList.length === 0" description="No orders"/>
      </van-list>
    </div>

    <!-- 接单确认对话框 -->
    <van-dialog
      v-model:show="showReceiveConfirm"
      title=""
      show-cancel-button
      confirm-button-text="Confirm"
      cancel-button-text="Cancel"
      @confirm="confirmReceive"
      @cancel="cancelReceive"
      class="receive-task-dialog"
    >
      <div class="confirm-dialog-content">
        <div class="dialog-header">
          <div class="dialog-icon-wrapper">
            <van-icon name="gold-coin-o" class="dialog-icon"/>
          </div>
          <h3 class="dialog-title">Receive Task</h3>
          <p class="dialog-subtitle">Confirm to receive this task</p>
        </div>
        
        <div v-if="selectedTask" class="task-summary-card">
          <div class="summary-item">
            <div class="summary-icon-wrapper">
              <van-icon name="orders-o" class="summary-icon"/>
            </div>
            <div class="summary-content">
              <span class="summary-label">Batch ID</span>
              <span class="summary-value">{{ selectedTask.batchId }}</span>
            </div>
          </div>
          
          <div class="summary-item highlight">
            <div class="summary-icon-wrapper highlight-icon">
              <span class="reward-currency-icon-small">{{ getCurrency() }}</span>
            </div>
            <div class="summary-content">
              <span class="summary-label">Total Reward</span>
              <span class="summary-value highlight-value">
                {{ formatMoney(calculateTotalReward(selectedTask)) }}
              </span>
            </div>
          </div>
          
          <div class="summary-item">
            <div class="summary-icon-wrapper">
              <van-icon name="friends-o" class="summary-icon"/>
            </div>
            <div class="summary-content">
              <span class="summary-label">Available Accounts</span>
              <span class="summary-value">{{ selectedTask.totalAccountCount - selectedTask.receivedAccountCount }}</span>
            </div>
          </div>
          
          <div class="summary-item">
            <div class="summary-icon-wrapper">
              <van-icon name="gold-coin-o" class="summary-icon"/>
            </div>
            <div class="summary-content">
              <span class="summary-label">Unit Price</span>
              <span class="summary-value">{{ formatMoney(getTaskUnitPrice(selectedTask)) }}</span>
            </div>
          </div>
        </div>
      </div>
    </van-dialog>

    <!-- 订单详情/处理任务弹窗 -->
    <van-popup
      v-model:show="showOrderDetail"
      position="bottom"
      :style="{ height: '90%' }"
      round
      closeable
      close-icon-position="top-right"
    >
      <div class="order-detail-popup" v-if="selectedOrder">
        <div class="popup-header-new">
          <h2>Task Processing</h2>
        </div>

        <div class="popup-content-new">
          <!-- 顶部：进度和奖励（可折叠历史账号） -->
          <div v-if="selectedOrder.orderState === 'received' && progressData" class="top-section">
            <!-- 进度条和奖励 -->
            <div class="progress-reward-card">
              <div class="progress-info-top">
                <div class="progress-stats-top">
                  <span class="progress-text-top">
                    {{ progressData.completedCount }}/{{ progressData.totalCount }} 
                    ({{ progressData.successCount }}✓ {{ progressData.failedCount }}✗)
                  </span>
                </div>
                <div class="reward-estimate">
                  <span class="reward-label">Est. Reward:</span>
                  <span class="reward-value">{{ formatMoney(calculateOrderReward(selectedOrder)) }}</span>
                </div>
              </div>
              <van-progress 
                :percentage="progressData.completedCount > 0 ? (progressData.completedCount / progressData.totalCount) * 100 : 0" 
                :show-pivot="false"
                color="#ff9f00"
                stroke-width="8"
                style="margin-top: 8px"
              />
            </div>

            <!-- 可折叠的历史账号列表 -->
            <div class="history-accounts-section">
              <div class="history-header" @click="toggleHistoryAccounts">
                <span class="history-title">Account History</span>
                <van-icon 
                  :name="showHistoryAccounts ? 'arrow-up' : 'arrow-down'" 
                  class="history-expand-icon"
                />
              </div>
              <div v-if="showHistoryAccounts && progressData.accounts" class="history-accounts-list">
                <div 
                  v-for="(account, index) in progressData.accounts" 
                  :key="index"
                  class="history-account-item"
                  :class="{
                    'status-success': account.status === 'success',
                    'status-failed': account.status === 'failed',
                    'status-processing': account.status === 'processing',
                    'status-locked': !account.canProcess && account.status !== 'success' && account.status !== 'failed'
                  }"
                >
                  <div class="history-account-main">
                    <span class="history-account-index">{{ index + 1 }}.</span>
                    <span class="history-account-name">{{ account.account }}</span>
                    <van-tag 
                      v-if="account.status === 'success'" 
                      type="success"
                      size="mini"
                    >✓</van-tag>
                    <van-tag 
                      v-else-if="account.status === 'failed'" 
                      type="danger"
                      size="mini"
                    >✗</van-tag>
                    <van-tag 
                      v-else-if="account.status === 'processing'" 
                      type="warning"
                      size="mini"
                    >●</van-tag>
                    <van-icon v-else name="lock" size="14px" />
                  </div>
                  <div v-if="account.status === 'failed' && account.processResult" 
                       class="history-failed-reason">
                    {{ account.processResult }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 核心内容：当前账号和话术 -->
          <div v-if="selectedOrder.orderState === 'received'" class="current-task-section">
            <div v-if="progressData && progressData.currentIndex !== null && progressData.currentIndex >= 0" class="current-account-section">
              <!-- 当前账号 -->
              <div class="current-account-card">
                <div class="current-account-header">
                  <van-icon name="user-circle-o" class="account-icon"/>
                  <div class="current-account-info">
                    <div class="current-account-label">Current Account</div>
                    <div class="current-account-name">
                      {{ progressData.accounts[progressData.currentIndex]?.account }}
                    </div>
                  </div>
                  <div class="account-actions">
                    <van-button 
                      type="default" 
                      size="mini" 
                      plain 
                      icon="copy"
                      @click.stop="copyCurrentAccount"
                      class="copy-account-btn"
                    >
                      Copy
                    </van-button>
                    <van-button 
                      type="primary" 
                      size="mini" 
                      icon="chat"
                      @click.stop="openWhatsApp"
                      class="whatsapp-btn"
                    >
                      WhatsApp
                    </van-button>
                  </div>
                  <van-tag type="warning" size="small">Processing</van-tag>
                </div>
              </div>

              <!-- 当前账号的话术 -->
              <div class="current-speech-section">
                <div class="speech-header">
                  <van-icon name="chat-o" class="speech-icon"/>
                  <span class="speech-title">Speech Skills</span>
                  <van-button 
                    v-if="speechData && speechData.accountSpeechList"
                    type="primary" 
                    size="mini"
                    plain
                    @click="loadSpeech"
                    class="refresh-speech-btn"
                  >
                    Refresh
                  </van-button>
                </div>
                <div v-if="speechData && speechData.accountSpeechList" class="speech-content-current">
                  <div 
                    v-for="(speechItem, idx) in speechData.accountSpeechList" 
                    :key="idx"
                    v-show="speechItem.account === progressData.accounts[progressData.currentIndex]?.account"
                    class="current-speech-list"
                  >
                    <div 
                      v-for="(speech, sIdx) in speechItem.speeches" 
                      :key="sIdx"
                      class="speech-item-current"
                    >
                      <div class="speech-text-current">{{ speech }}</div>
                      <van-button 
                        type="default" 
                        size="mini" 
                        plain 
                        icon="copy"
                        @click="copyText(speech)"
                      >
                        Copy
                      </van-button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 成功/失败按钮 -->
              <div class="action-buttons-section">
                <div class="action-buttons-row">
                  <van-button 
                    type="danger" 
                    size="large"
                    block
                    round
                    icon="cross"
                    @click="showFailedDialog(progressData.currentIndex)"
                    class="failed-btn-inline"
                  >
                    Failed
                  </van-button>
                  <van-button 
                    type="success" 
                    size="large"
                    block
                    round
                    icon="success"
                    @click="markAccountResult(progressData.currentIndex, 'success')"
                    class="success-btn-inline"
                  >
                    Success
                  </van-button>
                </div>
              </div>
            </div>
            <div v-else-if="progressData && progressData.completedCount === progressData.totalCount" class="all-completed-message">
              <van-icon name="success" size="48px" color="#ff9f00"/>
              <p>All accounts have been processed!</p>
              <!-- 显示凭证上传 -->
              <div class="evidence-section-bottom" v-if="progressData.canSubmit">
                <h3>Upload Evidence</h3>
                <van-field
                  v-model="evidenceDesc"
                  type="textarea"
                  placeholder="Evidence description (optional)"
                  rows="3"
                />
                <van-uploader
                  v-model="evidenceFileList"
                  :max-count="1"
                  :after-read="afterRead"
                  accept="image/*,video/*"
                >
                  <template #preview-cover="{ file }">
                    <div class="preview-cover">
                      <van-icon name="delete" @click.stop="removeFile(file)"/>
                    </div>
                  </template>
                </van-uploader>
                <van-button
                  type="primary"
                  round
                  block
                  :loading="uploading"
                  @click="handleSubmitEvidence"
                  style="margin-top: 16px"
                >
                  Submit Evidence
                </van-button>
              </div>
            </div>
          </div>

          <!-- 已上传的凭证 -->
          <div v-if="selectedOrder.evidenceUrl" class="evidence-preview">
            <h3>Evidence</h3>
            <van-image
              v-if="isImage(selectedOrder.evidenceUrl)"
              :src="getImageUrl(selectedOrder.evidenceUrl)"
              fit="cover"
              style="width: 100%; max-height: 300px; margin-top: 8px"
            />
            <video
              v-else
              :src="getImageUrl(selectedOrder.evidenceUrl)"
              controls
              style="width: 100%; max-height: 300px; margin-top: 8px"
            />
            <p v-if="selectedOrder.evidenceDesc" class="evidence-desc">{{ selectedOrder.evidenceDesc }}</p>
          </div>
        </div>
      </div>
    </van-popup>

    <!-- 失败原因输入对话框 -->
    <van-dialog
      v-model:show="showFailedReasonDialog"
      title="Mark as Failed"
      show-cancel-button
      confirm-button-text="Confirm"
      cancel-button-text="Cancel"
      @confirm="confirmMarkFailed"
      @cancel="() => { showFailedReasonDialog = false; failedReason = '' }"
    >
      <div style="padding: 16px">
        <van-field
          v-model="failedReason"
          type="textarea"
          placeholder="Please enter the reason for failure (optional)"
          rows="3"
        />
      </div>
    </van-dialog>

    <!-- Toast 提示 -->
    <van-toast v-model:show="showSuccessToast" type="success" message="Task received successfully!"/>
    <van-toast v-model:show="showFailToast" type="fail" :message="failMessage"/>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { 
  getAvailableContactTasks, 
  receiveContactTask,
  listContactTaskOrders, 
  getContactTaskOrder, 
  uploadContactTaskEvidence, 
  getContactTaskOrderSpeech,
  getContactTaskOrderProgress,
  markContactTaskAccountResult, getCurrency
} from '@/utils/api'
import { getTips } from '@/utils/api'
import apiConfig from '@/config/api'

export default {
  name: 'ContactTaskView',
  methods: {getCurrency},
  setup() {
    const route = useRoute()
    
    // Tab 状态 - 根据路由参数初始化
    const activeTab = ref(route.query.tab === 'orders' ? 'orders' : 'tasks')
    const orderStateTab = ref('received')

    // 接单相关状态
    const taskLoading = ref(false)
    const taskFinished = ref(false)
    const taskList = ref([])
    const taskQueryParams = ref({
      pageNum: 1,
      pageSize: 10
    })
    const showReceiveConfirm = ref(false)
    const selectedTask = ref(null)
    const receivingTaskId = ref(null)

    // 做单相关状态
    const orderLoading = ref(false)
    const orderFinished = ref(false)
    const ordersList = ref([])
    const orderQueryParams = ref({
      pageNum: 1,
      pageSize: 10,
      orderState: 'received'
    })
    const showOrderDetail = ref(false)
    const selectedOrder = ref(null)
    const evidenceFileList = ref([])
    const evidenceDesc = ref('')
    const uploading = ref(false)
    const speechData = ref(null)
    const progressData = ref(null)
    const showFailedReasonDialog = ref(false)
    const failedReason = ref('')
    const pendingFailedIndex = ref(-1)
    const showHistoryAccounts = ref(false)

    // Toast 状态
    const showSuccessToast = ref(false)
    const showFailToast = ref(false)
    const failMessage = ref('')

    // 账号展开状态管理（key: orderId）
    const expandedAccounts = ref(new Set())
    
    // 任务详情展开状态管理（key: taskId）
    const expandedTasks = ref(new Set())

    // 通用方法
    const formatMoney = (amount) => {
      if (!amount && amount !== 0) return '0.00'
      let num = 0
      if (typeof amount === 'object' && amount.toString) {
        num = parseFloat(amount.toString()) || 0
      } else {
        num = parseFloat(amount) || 0
      }
      return num.toFixed(2)
    }


    const formatTime = (time) => {
      if (!time) return '-'
      return new Date(time).toLocaleString()
    }

    // 接单相关方法
    const getTaskUnitPrice = (task) => task?.effectiveUnitPrice ?? task?.unitPrice

    const calculateTotalReward = (task) => {
      if (!task) return 0
      const unitPrice = getTaskUnitPrice(task)
      const p = unitPrice != null ? (typeof unitPrice === 'object' && unitPrice.toString ? parseFloat(unitPrice.toString()) : parseFloat(unitPrice)) : NaN
      if (Number.isNaN(p)) return 0
      const availableCount = (task.totalAccountCount || 0) - (task.receivedAccountCount || 0)
      return p * availableCount
    }

    const getOrderUnitPrice = (order) => order?.unitPrice ?? order?.task?.unitPrice

    const getSpeechPreview = (speechSkills) => {
      if (!speechSkills) return 'No speech skills'
      try {
        const speeches = JSON.parse(speechSkills)
        if (Array.isArray(speeches) && speeches.length > 0) {
          return speeches[0].substring(0, 50) + (speeches[0].length > 50 ? '...' : '')
        }
      } catch (e) {
        const lines = speechSkills.split('\n')
        return lines[0].substring(0, 50) + (lines[0].length > 50 ? '...' : '')
      }
      return 'No speech skills'
    }

    const onLoadTasks = async () => {
      if (taskFinished.value) return
      try {
        taskLoading.value = true
        const response = await getAvailableContactTasks(taskQueryParams.value)
        
        if (response.rows && response.rows.length > 0) {
          const firstNewItemId = response.rows[0]?.id
          const isDuplicate = taskList.value.length > 0 && taskList.value.some(item => item.id === firstNewItemId)
          
          if (isDuplicate) {
            taskFinished.value = true
          } else {
            taskList.value = [...taskList.value, ...response.rows]
            if (response.rows.length < taskQueryParams.value.pageSize) {
              taskFinished.value = true
            }
          }
        } else {
          taskFinished.value = true
        }
        
        taskQueryParams.value.pageNum++
      } catch (error) {
        console.error('Error loading tasks:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      } finally {
        taskLoading.value = false
      }
    }

    const handleReceive = (task) => {
      const availableCount = task.totalAccountCount - task.receivedAccountCount
      if (availableCount <= 0) {
        showFailToast.value = true
        failMessage.value = 'No available accounts'
        return
      }
      selectedTask.value = task
      showReceiveConfirm.value = true
    }

    const confirmReceive = async () => {
      if (!selectedTask.value) return
      try {
        receivingTaskId.value = selectedTask.value.id
        await receiveContactTask(selectedTask.value.id)
        showSuccessToast.value = true
        showReceiveConfirm.value = false
        
        // 刷新任务列表
        taskList.value = []
        taskQueryParams.value.pageNum = 1
        taskFinished.value = false
        await onLoadTasks()
        
        // 切换到订单 tab 并刷新
        activeTab.value = 'orders'
        ordersList.value = []
        orderQueryParams.value.pageNum = 1
        orderFinished.value = false
        await onLoadOrders()
      } catch (error) {
        console.error('Error receiving task:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      } finally {
        receivingTaskId.value = null
        selectedTask.value = null
      }
    }

    const cancelReceive = () => {
      showReceiveConfirm.value = false
      selectedTask.value = null
    }

    // 做单相关方法
    const getStatusClass = (state) => {
      const map = {
        'received': 'status-received',
        'submitted': 'status-submitted',
        'passed': 'status-passed',
        'rejected': 'status-rejected'
      }
      return map[state] || 'status-default'
    }

    const getStatusIcon = (state) => {
      const map = {
        'received': 'clock-o',
        'submitted': 'passed',
        'passed': 'checked',
        'rejected': 'close'
      }
      return map[state] || 'info-o'
    }

    const getStatusText = (state) => {
      const map = {
        'received': 'Received',
        'submitted': 'Submitted',
        'passed': 'Passed',
        'rejected': 'Rejected'
      }
      return map[state] || state
    }

    const getAccountCount = (accounts) => {
      if (!accounts) return 0
      return accounts.split(',').filter(acc => acc.trim()).length
    }

    const getAccountList = (accounts) => {
      if (!accounts) return []
      return accounts.split(',').map(acc => acc.trim()).filter(acc => acc)
    }

    const toggleAccounts = (orderId) => {
      if (expandedAccounts.value.has(orderId)) {
        expandedAccounts.value.delete(orderId)
      } else {
        expandedAccounts.value.add(orderId)
        // 如果展开，尝试加载进度数据
        const order = ordersList.value.find(o => o.id === orderId)
        if (order && order.orderState === 'received' && !orderProgressCache.value[orderId]) {
          loadOrderProgress(orderId)
        }
      }
    }

    // 订单进度数据缓存（key: orderId）
    const orderProgressCache = ref({})

    const loadOrderProgress = async (orderId) => {
      if (orderProgressCache.value[orderId]) {
        return
      }
      try {
        const response = await getContactTaskOrderProgress(orderId)
        orderProgressCache.value[orderId] = response.data
      } catch (error) {
        console.error('Error loading order progress:', error)
      }
    }

    const getAccountStatus = (orderId, account) => {
      const progress = orderProgressCache.value[orderId]
      if (!progress || !progress.accounts) return null
      const accountItem = progress.accounts.find(item => item.account === account)
      return accountItem ? accountItem.status : null
    }

    const getAccountStatusTagType = (status) => {
      if (status === 'success') return 'success'
      if (status === 'failed') return 'danger'
      if (status === 'processing') return 'warning'
      return 'default'
    }

    const getAccountStatusText = (status) => {
      if (status === 'success') return '✓'
      if (status === 'failed') return '✗'
      if (status === 'processing') return '●'
      return ''
    }

    const toggleHistoryAccounts = () => {
      showHistoryAccounts.value = !showHistoryAccounts.value
    }

    // 切换任务详情展开/收起
    const toggleTaskDetail = (taskId) => {
      if (expandedTasks.value.has(taskId)) {
        expandedTasks.value.delete(taskId)
      } else {
        expandedTasks.value.add(taskId)
      }
    }

    const calculateOrderReward = (order) => {
      const unitPrice = getOrderUnitPrice(order)
      if (unitPrice == null) return order?.totalAmount ?? 0
      const count = getAccountCount(order.accounts)
      const p = typeof unitPrice === 'object' ? parseFloat(unitPrice.toString()) : parseFloat(unitPrice)
      return Number.isNaN(p) ? (order?.totalAmount ?? 0) : p * count
    }

    watch(orderStateTab, (newVal) => {
      orderQueryParams.value.orderState = newVal
      orderQueryParams.value.pageNum = 1
      ordersList.value = []
      orderFinished.value = false
      onLoadOrders()
    })

    const onLoadOrders = async () => {
      if (orderFinished.value) return
      try {
        orderLoading.value = true
        const response = await listContactTaskOrders(orderQueryParams.value)
        
        if (response.rows && response.rows.length > 0) {
          const firstNewItemId = response.rows[0]?.id
          const isDuplicate = ordersList.value.length > 0 && ordersList.value.some(item => item.id === firstNewItemId)
          
          if (isDuplicate) {
            orderFinished.value = true
          } else {
            ordersList.value = [...ordersList.value, ...response.rows]
            if (response.rows.length < orderQueryParams.value.pageSize) {
              orderFinished.value = true
            }
          }
        } else {
          orderFinished.value = true
        }
        
        orderQueryParams.value.pageNum++
      } catch (error) {
        console.error('Error loading orders:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      } finally {
        orderLoading.value = false
      }
    }

    const handleViewOrder = async (order) => {
      try {
        const response = await getContactTaskOrder(order.id)
        selectedOrder.value = response.data
        evidenceFileList.value = []
        evidenceDesc.value = ''
        speechData.value = null
        progressData.value = null
        showOrderDetail.value = true
        // 自动加载进度
        if (selectedOrder.value.orderState === 'received') {
          await loadProgress()
        }
        // 自动加载话术
        await loadSpeech()
      } catch (error) {
        console.error('Error loading order detail:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      }
    }

    const loadProgress = async () => {
      if (!selectedOrder.value) return
      try {
        const response = await getContactTaskOrderProgress(selectedOrder.value.id)
        progressData.value = response.data
      } catch (error) {
        console.error('Error loading progress:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      }
    }

    const markAccountResult = async (index, result) => {
      if (result === 'failed') {
        showFailedReasonDialog.value = true
        pendingFailedIndex.value = index
        return
      }
      
      try {
        await markContactTaskAccountResult(selectedOrder.value.id, index, result, null)
        showSuccessToast.value = true
        failMessage.value = 'Account result marked successfully'
        await loadProgress()
      } catch (error) {
        console.error('Error marking account result:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      }
    }

    const showFailedDialog = (index) => {
      pendingFailedIndex.value = index
      showFailedReasonDialog.value = true
    }

    const confirmMarkFailed = async () => {
      try {
        await markContactTaskAccountResult(
          selectedOrder.value.id, 
          pendingFailedIndex.value, 
          'failed', 
          failedReason.value || null
        )
        showSuccessToast.value = true
        failMessage.value = 'Account marked as failed'
        showFailedReasonDialog.value = false
        failedReason.value = ''
        await loadProgress()
      } catch (error) {
        console.error('Error marking account as failed:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      }
    }

    const handleUploadEvidence = async (order) => {
      await handleViewOrder(order)
    }

    const loadSpeech = async () => {
      if (!selectedOrder.value) return
      try {
        const response = await getContactTaskOrderSpeech(selectedOrder.value.id)
        speechData.value = response.data
      } catch (error) {
        console.error('Error loading speech:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      }
    }

    const copyText = async (text) => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          showSuccessToast.value = true
          failMessage.value = 'Copied to clipboard'
        } else {
          // 降级方案：使用传统方法
          const textArea = document.createElement('textarea')
          textArea.value = text
          textArea.style.position = 'fixed'
          textArea.style.opacity = '0'
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
          showSuccessToast.value = true
          failMessage.value = 'Copied to clipboard'
        }
      } catch (error) {
        console.error('Error copying text:', error)
        failMessage.value = 'Failed to copy'
        showFailToast.value = true
      }
    }

    const copyAccountSpeech = async (accountSpeech) => {
      if (!accountSpeech || !accountSpeech.speeches || accountSpeech.speeches.length === 0) {
        failMessage.value = 'No speech to copy'
        showFailToast.value = true
        return
      }
      // 将所有话术合并，每个话术一行
      const allSpeech = accountSpeech.speeches.join('\n')
      await copyText(allSpeech)
    }

    // 复制当前账号
    const copyCurrentAccount = async () => {
      if (!progressData.value || progressData.value.currentIndex === null) {
        failMessage.value = 'No current account'
        showFailToast.value = true
        return
      }
      const currentAccount = progressData.value.accounts[progressData.value.currentIndex]?.account
      if (!currentAccount) {
        failMessage.value = 'No current account'
        showFailToast.value = true
        return
      }
      await copyText(currentAccount)
    }

    // 格式化电话号码
    const formatPhoneNumber = (account) => {
      // 移除所有非数字字符（保留 +）
      let phone = account.replace(/[^\d+]/g, '')
      // 如果以 + 开头，移除 +（wa.me 不需要 +）
      if (phone.startsWith('+')) {
        phone = phone.substring(1)
      }
      // 返回纯数字格式
      return phone.replace(/\D/g, '')
    }

    // 合并话术为一条消息
    const mergeSpeech = (speeches) => {
      if (!speeches || speeches.length === 0) return ''
      // 使用换行分隔多个话术
      return speeches.join('\n')
    }

    // 打开 WhatsApp（带话术）
    const openWhatsApp = async () => {
      if (!progressData.value || progressData.value.currentIndex === null) {
        failMessage.value = 'No current account'
        showFailToast.value = true
        return
      }
      
      const currentAccount = progressData.value.accounts[progressData.value.currentIndex]?.account
      if (!currentAccount) {
        failMessage.value = 'No current account'
        showFailToast.value = true
        return
      }
      
      // 获取当前账号的话术
      let speechText = ''
      if (speechData.value && speechData.value.accountSpeechList) {
        const currentSpeechItem = speechData.value.accountSpeechList.find(
          item => item.account === currentAccount
        )
        if (currentSpeechItem && currentSpeechItem.speeches && currentSpeechItem.speeches.length > 0) {
          // 将所有话术合并为一条消息
          speechText = mergeSpeech(currentSpeechItem.speeches)
        }
      }
      
      // 格式化电话号码
      const phoneNumber = formatPhoneNumber(currentAccount)
      
      if (!phoneNumber) {
        failMessage.value = 'Invalid phone number format'
        showFailToast.value = true
        return
      }
      
      // 构建 WhatsApp URL
      const encodedText = speechText ? encodeURIComponent(speechText) : ''
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      
      try {
        if (isMobile) {
          // 移动端：先尝试 App 协议
          const appUrl = speechText
            ? `whatsapp://send?phone=${phoneNumber}&text=${encodedText}`
            : `whatsapp://send?phone=${phoneNumber}`
          
          // 创建隐藏 iframe 尝试打开 WhatsApp App
          const iframe = document.createElement('iframe')
          iframe.style.display = 'none'
          iframe.src = appUrl
          document.body.appendChild(iframe)
          
          // 设置超时降级到 Web URL
          setTimeout(() => {
            try {
              document.body.removeChild(iframe)
            } catch (e) {
              // iframe 可能已被移除
            }
            const webUrl = speechText
              ? `https://wa.me/${phoneNumber}?text=${encodedText}`
              : `https://wa.me/${phoneNumber}`
            window.location.href = webUrl
          }, 2000)
        } else {
          // 桌面端：直接使用 Web URL
          const webUrl = speechText
            ? `https://wa.me/${phoneNumber}?text=${encodedText}`
            : `https://wa.me/${phoneNumber}`
          window.open(webUrl, '_blank')
        }
        
        // 如果话术存在，也复制到剪贴板作为备用
        if (speechText) {
          await copyText(speechText)
        }
      } catch (error) {
        console.error('Error opening WhatsApp:', error)
        // 降级方案：只复制话术（如果存在）
        if (speechText) {
          await copyText(speechText)
          failMessage.value = 'WhatsApp failed to open, speech copied to clipboard'
        } else {
          failMessage.value = 'Failed to open WhatsApp'
        }
        showFailToast.value = true
      }
    }

    const afterRead = () => {
      // 文件已选择，等待上传
    }

    const removeFile = (file) => {
      const index = evidenceFileList.value.indexOf(file)
      if (index > -1) {
        evidenceFileList.value.splice(index, 1)
      }
    }

    const handleSubmitEvidence = async () => {
      if (!selectedOrder.value) return
      if (evidenceFileList.value.length === 0) {
        failMessage.value = 'Please select a file to upload'
        showFailToast.value = true
        return
      }

      try {
        uploading.value = true
        const file = evidenceFileList.value[0].file
        const desc = evidenceDesc.value || null
        
        await uploadContactTaskEvidence(selectedOrder.value.id, file, desc)
        showSuccessToast.value = true
        showOrderDetail.value = false
        
        // 刷新订单列表
        ordersList.value = []
        orderQueryParams.value.pageNum = 1
        orderFinished.value = false
        await onLoadOrders()
      } catch (error) {
        console.error('Error uploading evidence:', error)
        failMessage.value = getTips(error)
        showFailToast.value = true
      } finally {
        uploading.value = false
      }
    }

    const isImage = (url) => {
      if (!url) return false
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
      return imageExtensions.some(ext => url.toLowerCase().includes(ext))
    }

    const getImageUrl = (url) => {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      // 拼接完整 URL，参考 pay_order 的方式
      return apiConfig.baseUrl + url
    }

    // 监听 tab 切换，加载对应数据
    watch(activeTab, (newVal) => {
      if (newVal === 'tasks') {
        if (taskList.value.length === 0) {
          taskList.value = []
          taskQueryParams.value.pageNum = 1
          taskFinished.value = false
          onLoadTasks()
        }
      } else if (newVal === 'orders') {
        if (ordersList.value.length === 0) {
          ordersList.value = []
          orderQueryParams.value.pageNum = 1
          orderFinished.value = false
          onLoadOrders()
        }
      }
    })

    onMounted(() => {
      // 根据路由参数决定加载哪个标签的数据
      if (route.query.tab === 'orders') {
        // 如果路由参数指定了 orders 标签，加载订单列表
        onLoadOrders()
      } else {
        // 默认加载任务列表
        onLoadTasks()
      }
    })

    return {
      activeTab,
      orderStateTab,
      taskLoading,
      taskFinished,
      taskList,
      showReceiveConfirm,
      selectedTask,
      receivingTaskId,
      orderLoading,
      orderFinished,
      ordersList,
      showOrderDetail,
      selectedOrder,
      evidenceFileList,
      evidenceDesc,
      uploading,
      speechData,
      progressData,
      showFailedReasonDialog,
      failedReason,
      showSuccessToast,
      showFailToast,
      failMessage,
      formatMoney,
      formatTime,
      calculateTotalReward,
      calculateOrderReward,
      getSpeechPreview,
      getStatusClass,
      getStatusIcon,
      getStatusText,
      getAccountCount,
      onLoadTasks,
      handleReceive,
      confirmReceive,
      cancelReceive,
      onLoadOrders,
      handleViewOrder,
      handleUploadEvidence,
      loadSpeech,
      afterRead,
      removeFile,
      handleSubmitEvidence,
      isImage,
      getImageUrl,
      copyText,
      copyAccountSpeech,
      copyCurrentAccount,
      openWhatsApp,
      loadProgress,
      markAccountResult,
      showFailedDialog,
      confirmMarkFailed,
      getAccountList,
      toggleAccounts,
      expandedAccounts,
      getAccountStatus,
      getAccountStatusTagType,
      getAccountStatusText,
      showHistoryAccounts,
      toggleHistoryAccounts,
      expandedTasks,
      toggleTaskDetail
    }
  }
}
</script>

<style scoped>
.contact-task-view {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
}

.header {
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  padding: 20px 16px;
  color: white;
}

.header-left {
  display: flex;
  flex-direction: column;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
}

.custom-tabs {
  background: white;
  padding: 0 16px;
}

.order-state-tabs {
  background: white;
  padding: 0 16px;
  margin-top: 8px;
}

.tasks-tab,
.orders-tab {
  padding: 16px;
}

.task-list,
.order-list {
  margin-top: 16px;
}

.task-card,
.order-card {
  background: white;
  border-radius: 20px;
  padding: 0;
  margin-bottom: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 159, 0, 0.1);
}

.task-card:hover {
  box-shadow: 0 6px 24px rgba(255, 159, 0, 0.15);
  transform: translateY(-2px);
}

.order-card {
  border: 1px solid #e9ecef;
}

.order-card:active {
  transform: scale(0.98);
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 12px;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  border-bottom: 1px solid #f0f0f0;
}

.card-header-order {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 14px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #e9ecef;
}

.order-header-left {
  flex: 1;
}

.order-no-row,
.batch-id-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.order-no-row:last-child,
.batch-id-row:last-child {
  margin-bottom: 0;
}

.order-label,
.batch-label {
  font-size: 12px;
  color: #999;
  font-weight: 500;
}

.order-number {
  font-size: 14px;
  color: #ff9f00;
  font-weight: 600;
  background: rgba(102, 126, 234, 0.1);
  padding: 2px 8px;
  border-radius: 4px;
}

.batch-number {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.status-badge-order {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.status-badge-order.received {
  background: #e3f2fd;
  color: #1976d2;
}

.status-badge-order.submitted {
  background: #fff3e0;
  color: #f57c00;
}

.status-badge-order.passed {
  background: #e8f5e9;
  color: #388e3c;
}

.status-badge-order.rejected {
  background: #ffebee;
  color: #d32f2f;
}

.batch-info {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  flex-wrap: nowrap;
}

.task-detail-toggle-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(255, 159, 0, 0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  flex-shrink: 0;
  white-space: nowrap;
}

.task-detail-toggle-inline:hover {
  background: rgba(255, 159, 0, 0.2);
}

.task-detail-toggle-inline:active {
  background: rgba(255, 159, 0, 0.15);
}

.toggle-info-icon-small {
  font-size: 14px;
  color: #ff9f00;
  transition: transform 0.3s ease;
}

.task-detail-toggle-inline:hover .toggle-info-icon-small {
  transform: rotate(15deg);
}

.toggle-label-small {
  font-size: 12px;
  font-weight: 600;
  color: #ff9f00;
  letter-spacing: 0.3px;
  transition: color 0.3s ease;
}

.task-detail-toggle-inline:hover .toggle-label-small {
  color: #ff6b00;
}

.toggle-icon-small {
  font-size: 14px;
  color: #ff9f00;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(0deg);
}

.toggle-icon-small.expanded {
  transform: rotate(180deg);
  color: #ff6b00;
}

.batch-tag {
  font-weight: 600;
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.status-tag {
  font-weight: 500;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 6px;
  background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
  border: none;
  color: white;
  box-shadow: 0 2px 6px rgba(82, 196, 26, 0.3);
}

.reward-highlight {
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 30%, #ffa726 60%, #ff9800 100%);
  border-radius: 0;
  padding: 16px 20px;
  margin: 0;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(255, 159, 0, 0.2);
}

.reward-highlight::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%);
  animation: shimmer 4s infinite ease-in-out;
}

.reward-highlight::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.05) 50%, transparent 100%);
  pointer-events: none;
}

@keyframes shimmer {
  0%, 100% { 
    transform: rotate(0deg) translate(0, 0);
    opacity: 0.8;
  }
  50% { 
    transform: rotate(180deg) translate(20px, 20px);
    opacity: 1;
  }
}

.reward-highlight-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;
  flex-wrap: nowrap;
}

.reward-main-section {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
  flex: 1;
  min-width: 0;
}

.reward-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 14px;
  backdrop-filter: blur(10px);
  border: 2.5px solid rgba(255, 255, 255, 0.9);
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(255, 255, 255, 0.4);
}

.reward-icon {
  font-size: 24px;
  color: #fff;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.4));
}

.reward-currency-icon {
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  letter-spacing: 0;
}

.reward-amount-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.reward-label-row {
  display: flex;
  align-items: center;
}

.reward-highlight-label {
  font-size: 13px;
  opacity: 0.9;
  font-weight: 500;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.reward-amount-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.reward-currency {
  font-size: 24px;
  font-weight: 700;
  opacity: 0.95;
  line-height: 1;
}

.reward-highlight-amount {
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
  color: #fff200;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.3), 0 0 20px rgba(255, 242, 0, 0.4);
  letter-spacing: -1.5px;
}

.reward-equals {
  font-size: 20px;
  font-weight: 600;
  opacity: 0.9;
  margin: 0 2px;
}

.reward-account-count {
  font-size: 20px;
  font-weight: 700;
  opacity: 0.95;
}

.reward-multiply {
  font-size: 18px;
  font-weight: 600;
  opacity: 0.9;
  margin: 0 2px;
}

.reward-unit-price {
  font-size: 20px;
  font-weight: 700;
  opacity: 0.95;
}

.reward-detail-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.reward-detail-section-inline {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
  white-space: nowrap;
}

.reward-detail-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  white-space: nowrap;
}

.reward-detail-label {
  font-size: 10px;
  opacity: 0.85;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
}

.reward-detail-value {
  display: flex;
  align-items: baseline;
  gap: 2px;
  font-weight: 700;
  opacity: 0.95;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.reward-detail-currency {
  font-size: 16px;
}

.reward-detail-number {
  font-size: 14px;
}

.reward-detail-divider {
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.card-body {
  padding: 20px;
  margin: 0;
}

.task-detail-toggle {
  padding: 14px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
  position: relative;
}

.task-detail-toggle::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.task-detail-toggle:hover {
  background: linear-gradient(135deg, #f0f2f5 0%, #f8f9fa 100%);
  padding-left: 24px;
}

.task-detail-toggle:hover::before {
  opacity: 1;
}

.task-detail-toggle:active {
  background: linear-gradient(135deg, #e9ecef 0%, #f0f2f5 100%);
}

.toggle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toggle-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-info-icon {
  font-size: 16px;
  color: #ff9f00;
  transition: transform 0.3s ease;
}

.task-detail-toggle:hover .toggle-info-icon {
  transform: rotate(15deg);
}

.toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  letter-spacing: 0.3px;
  transition: color 0.3s ease;
}

.task-detail-toggle:hover .toggle-label {
  color: #ff9f00;
}

.toggle-icon {
  font-size: 18px;
  color: #ff9f00;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(0deg);
}

.toggle-icon.expanded {
  transform: rotate(180deg);
  color: #ff6b00;
}

/* 展开/收起动画 */
.slide-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.slide-fade-enter-to {
  opacity: 1;
  transform: translateY(0);
  max-height: 1000px;
}

.slide-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 1000px;
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.task-detail-content {
  padding: 20px;
  border-top: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
}

.card-body-order {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row-order {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  min-height: 28px;
}

.info-icon-order {
  font-size: 18px;
  color: #ff9f00;
  flex-shrink: 0;
  margin-top: 2px;
}

.reward-icon {
  color: #ffb300;
}

.reward-currency-icon-order {
  font-size: 18px;
  font-weight: 800;
  color: #ff9f00;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.label-order {
  font-size: 13px;
  color: #999;
  min-width: 80px;
  flex-shrink: 0;
}

.value-order {
  font-size: 14px;
  color: #333;
  flex: 1;
  text-align: right;
  word-break: break-word;
}

.reward-row {
  padding: 10px;
  background: linear-gradient(135deg, #fff9e6 0%, #ffffff 100%);
  border-radius: 8px;
  border: 1px solid #ffe082;
}

.reward-calculation {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  justify-content: flex-end;
}

.reward-formula {
  font-size: 13px;
  color: #666;
}

.reward-equals {
  font-size: 13px;
  color: #999;
}

.reward-total {
  font-size: 16px;
  color: #ff6b00;
  font-weight: 700;
}

.task-info-compact {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-row-compact {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #f0f0f0;
  transition: all 0.2s ease;
}

.info-row-compact:hover {
  background: linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%);
  border-color: #ff9f00;
  box-shadow: 0 2px 8px rgba(255, 159, 0, 0.1);
}

.info-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #fff5e6 0%, #ffe8cc 100%);
  border-radius: 10px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 159, 0, 0.2);
}

.info-icon-small {
  font-size: 18px;
  color: #ff9f00;
}

.info-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.info-label-compact {
  color: #999;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value-compact {
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  font-weight: 400;
}

.speech-preview {
  color: #666;
  font-style: italic;
}

.accounts-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 0;
}

.accounts-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  min-width: 60px;
  transition: all 0.2s ease;
}

.accounts-stat-item:hover {
  border-color: #ff9f00;
  box-shadow: 0 2px 8px rgba(255, 159, 0, 0.15);
  transform: translateY(-2px);
}

.accounts-stat-item.available-stat {
  background: linear-gradient(135deg, #fff5e6 0%, #ffe8cc 100%);
  border-color: #ffb74d;
}

.accounts-stat-item.available-stat:hover {
  border-color: #ff9f00;
  box-shadow: 0 2px 8px rgba(255, 159, 0, 0.25);
}

.accounts-stat-label {
  font-size: 11px;
  color: #999;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.accounts-stat-value {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

.accounts-stat-item.available-stat .accounts-stat-value.available-value {
  color: #ff6b00;
  font-weight: 800;
}

/* 一行显示的 Accounts 信息 */
.accounts-info-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 4px 0;
}

.accounts-item-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.accounts-label-inline {
  font-size: 13px;
  color: #666;
  font-weight: 400;
}

.accounts-value-inline {
  font-size: 14px;
  color: #333;
  font-weight: 700;
}

.accounts-item-inline.available-item-inline .accounts-label-inline {
  color: #ff9f00;
  font-weight: 500;
}

.accounts-item-inline.available-item-inline .accounts-value-inline.available-value-inline {
  color: #ff6b00;
  font-weight: 800;
}

.accounts-divider-inline {
  color: #ddd;
  font-size: 14px;
  margin: 0 2px;
}

.card-footer {
  padding: 20px;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  border-top: 1px solid #f0f0f0;
}

.receive-task-btn {
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(255, 159, 0, 0.4);
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.5px;
  height: 48px;
  transition: all 0.3s ease;
}

.receive-task-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 12px rgba(255, 159, 0, 0.5);
}

.receive-task-btn:disabled {
  background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%);
  box-shadow: none;
  opacity: 0.6;
}

.card-footer-order {
  padding: 12px 14px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.upload-evidence-btn {
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  font-weight: 600;
  letter-spacing: 0.5px;
}

.upload-evidence-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-no {
  font-size: 14px;
}

.batch-id {
  font-size: 12px;
  color: #666;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
}

.status-received {
  background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
  color: #ff9f00;
}

.status-submitted {
  background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
  color: #8b5cf6;
}

.status-passed {
  background: linear-gradient(135deg, #f3f0ff 0%, #ede9fe 100%);
  color: #ff9f00;
}

.status-rejected {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #ef4444;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-row:last-child {
  border-bottom: none;
}

.label {
  font-size: 14px;
  color: #666;
}

.value {
  font-size: 14px;
  color: #333;
  text-align: right;
  max-width: 60%;
  word-break: break-all;
  overflow-wrap: break-word;
}

.accounts-value {
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: top;
}

.task-desc {
  font-size: 12px;
  color: #999;
}

.reward-amount {
  color: #ff9f00;
  font-weight: 600;
}

.actual-amount {
  color: #388e3c;
  font-weight: 600;
}

.receive-task-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.confirm-dialog-content {
  padding: 0;
}

.dialog-header {
  text-align: center;
  padding: 16px 20px 14px;
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  color: white;
}

.dialog-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  margin-bottom: 8px;
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.dialog-icon {
  font-size: 24px;
  color: #fff;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.dialog-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: white;
}

.dialog-subtitle {
  font-size: 12px;
  opacity: 0.9;
  margin: 0;
  color: white;
}

.task-summary-card {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: linear-gradient(135deg, #fafbfc 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  transition: all 0.2s ease;
}

.summary-item:hover {
  border-color: #ff9f00;
  box-shadow: 0 2px 8px rgba(255, 159, 0, 0.1);
}

.summary-item.highlight {
  background: linear-gradient(135deg, #fff5e6 0%, #ffe8cc 100%);
  border-color: #ffb74d;
}

.summary-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #fff5e6 0%, #ffe8cc 100%);
  border-radius: 8px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 159, 0, 0.2);
}

.summary-icon-wrapper.highlight-icon {
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  border-color: #ff9f00;
}

.summary-icon {
  font-size: 18px;
  color: #ff9f00;
}

.reward-currency-icon-small {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.summary-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.summary-label {
  font-size: 11px;
  color: #999;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.summary-value {
  font-size: 15px;
  color: #333;
  font-weight: 700;
}

.summary-item.highlight .summary-value.highlight-value {
  font-size: 18px;
  color: #ff6b00;
  font-weight: 800;
}

.order-detail-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

.popup-header-new {
  padding: 14px 16px;
  background: white;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.popup-header-new h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.popup-content-new {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.popup-header {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.popup-header h2 {
  margin: 0;
  font-size: 20px;
}

.popup-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

/* 顶部进度和奖励区域 */
.top-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-reward-card {
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  border-radius: 12px;
  padding: 12px;
  color: white;
}

.progress-info-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-stats-top {
  font-size: 14px;
  opacity: 0.95;
}

.reward-estimate {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.reward-label {
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 4px;
}

.reward-value {
  font-size: 20px;
  font-weight: 700;
}

/* 历史账号折叠区域 */
.history-accounts-section {
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: linear-gradient(90deg, #f8f9fa 0%, #f0f2f5 100%);
  cursor: pointer;
  border-bottom: 1px solid #e8e8e8;
  transition: background 0.2s ease;
}

.history-header:active {
  background: linear-gradient(90deg, #e8e8e8 0%, #e0e0e0 100%);
}

.history-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.history-expand-icon {
  color: #ff9f00;
  font-size: 16px;
}

.history-accounts-list {
  padding: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.history-account-item {
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  margin-bottom: 4px;
  border-radius: 8px;
  background: #fafafa;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.history-account-main {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.history-account-item.status-success {
  background: linear-gradient(90deg, #f3f0ff 0%, #ede9fe 100%);
  border-left: 3px solid #ff9f00;
}

.history-account-item.status-failed {
  background: linear-gradient(90deg, #fef2f2 0%, #fee2e2 100%);
  border-left: 3px solid #ef4444;
}

.history-account-item.status-processing {
  background: linear-gradient(90deg, #f5f3ff 0%, #ede9fe 100%);
  border-left: 3px solid #8b5cf6;
}

.history-account-item.status-locked {
  background: #fafafa;
  opacity: 0.7;
  border-left: 3px solid #d9d9d9;
}

.history-account-index {
  color: #999;
  min-width: 24px;
  font-size: 12px;
}

.history-account-name {
  flex: 1;
  font-size: 13px;
  color: #333;
  word-break: break-all;
}

.history-failed-reason {
  width: 100%;
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
  padding-left: 24px;
  line-height: 1.5;
  word-break: break-word;
  background: rgba(239, 68, 68, 0.1);
  padding: 6px 12px;
  border-radius: 4px;
  margin-left: 24px;
  margin-right: 0;
}

/* 当前任务核心区域 */
.current-task-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.current-account-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.current-account-card {
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.current-account-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.account-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  margin-right: 8px;
}

.copy-account-btn,
.whatsapp-btn {
  flex-shrink: 0;
}

.account-icon {
  font-size: 32px;
  color: #ff9f00;
}

.current-account-info {
  flex: 1;
}

.current-account-label {
  font-size: 12px;
  color: #999;
  margin-bottom: 2px;
}

.current-account-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  word-break: break-all;
}

.current-speech-section {
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.speech-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.speech-icon {
  font-size: 20px;
  color: #ff9f00;
}

.speech-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex: 1;
}

.refresh-speech-btn {
  flex-shrink: 0;
}

.speech-content-current {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.current-speech-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.speech-item-current {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  background: linear-gradient(90deg, #fafbfc 0%, #f5f7fa 100%);
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  transition: all 0.2s ease;
}

.speech-item-current:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.speech-text-current {
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  word-break: break-word;
}

.action-buttons-section {
  padding: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.action-buttons-row {
  display: flex;
  gap: 10px;
}

.success-btn-inline {
  flex: 1;
  background: linear-gradient(135deg, #ff9f00 0%, #ffb74d 100%);
  border: none;
  color: white;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.2s ease;
}

.success-btn-inline:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #ff8c00 0%, #ff9f00 100%);
}

.failed-btn-inline {
  flex: 1;
  background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
  border: none;
  color: white;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
  transition: all 0.2s ease;
}

.failed-btn-inline:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
}

.all-completed-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.all-completed-message p {
  margin-top: 16px;
  font-size: 16px;
  color: #ff9f00;
  font-weight: 600;
}

.evidence-section-bottom {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
  width: 100%;
}

.evidence-section-bottom h3 {
  font-size: 16px;
  margin-bottom: 12px;
  color: #333;
}

.task-section,
.speech-section,
.evidence-section,
.evidence-preview {
  margin-top: 24px;
}

.task-section h3,
.speech-section h3,
.evidence-section h3,
.evidence-preview h3 {
  font-size: 16px;
  margin-bottom: 12px;
}

.task-info p {
  margin: 8px 0;
  font-size: 14px;
}

.speech-content {
  margin-top: 12px;
}

.account-speech-item {
  margin-bottom: 20px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #f0f0f0;
}

.account-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.account-header h4 {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #ff9f00;
}

.speech-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.speech-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.speech-text {
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  word-break: break-word;
}

.evidence-desc {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}

.accounts-cell-value {
  word-break: break-all;
  overflow-wrap: break-word;
  max-width: 100%;
  display: inline-block;
}

.skeletons {
  padding: 16px;
}

.van-empty {
  padding: 40px 0;
}

.accounts-row {
  cursor: pointer;
  padding: 10px 12px !important;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.accounts-row:active {
  background: #e9ecef;
}

.accounts-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: flex-end;
}

.accounts-count {
  color: #333;
  font-weight: 600;
  font-size: 14px;
}

.expand-icon {
  color: #ff9f00;
  font-size: 16px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.accounts-detail {
  margin-top: 6px;
  padding: 6px 0 6px 12px;
  border-left: 2px solid #e9ecef;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.account-item-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  font-size: 13px;
}

.account-index {
  color: #999;
  min-width: 24px;
  font-weight: 500;
}

.account-name {
  flex: 1;
  color: #333;
  word-break: break-all;
}
</style>
